import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  addDoc,
  where,
} from "firebase/firestore";
import { firestore } from "firebaseConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { sendDietChartWhatsAppNotification } from "services/dietchart";
import { WHATSAPP_TEMPLATE_TYPES } from "constant";
import {
  LAYOUT_VARIANT_PER_DAY,
  validatePerDayDateRange,
  PER_DAY_SOFT_MAX_RECIPES_PER_SLOT,
} from "utils/perDayDietChart";

const META = new Set([
  "from",
  "to",
  "notes",
  "clientName",
  "dietChartId",
  "weekPlan",
  "chartType",
  "createdAt",
  "clientId",
  "date",
  "layoutVariant",
  "dayPlans",
  "dayScheduleKeys",
  "selected",
]);

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((k) => {
      const next = stripUndefinedDeep(value[k]);
      if (next !== undefined) out[k] = next;
    });
    return out;
  }
  if (value === undefined) return undefined;
  return value;
}

async function processRecipeRow(recipeRow, uid) {
  const storage = getStorage();
  const data = stripUndefinedDeep({
    name: recipeRow.name?.label
      ? recipeRow.name?.value
      : recipeRow.name,
    instanceId: recipeRow.instanceId,
    andRecipe: recipeRow.andRecipe ?? null,
    alternativeName: recipeRow.alternativeName ?? null,
    ingredients: recipeRow.ingredients,
    groceryList: recipeRow.groceryList ?? [],
    nutritions: recipeRow.nutritions ?? [],
    description: recipeRow.description ? recipeRow.description : "",
    preparation: recipeRow.preparation ? recipeRow.preparation : "",
    howToTake: recipeRow.howToTake,
    foodCategory:
      recipeRow.foodCategory?.value ?? recipeRow.foodCategory,
    calories: parseFloat(recipeRow.calories),
    weight: parseFloat(recipeRow.weight),
    foodType: recipeRow.foodType ?? [],
    unit: recipeRow.unit?.value ?? recipeRow.unit,
    qty: recipeRow.qty,
    toggleMode:
      recipeRow.toggleMode && recipeRow.toggleMode !== ""
        ? recipeRow.toggleMode
        : "",
    image: recipeRow.image ? recipeRow.image : null,
  });

  if (
    recipeRow.image &&
    typeof recipeRow.image !== "string" &&
    recipeRow.image instanceof File
  ) {
    const imageRef = ref(
      storage,
      `recipes/${uid}/${Date.now()}_${recipeRow.image.name}`
    );
    const snapshot = await uploadBytes(imageRef, recipeRow.image);
    const imageUrl = await getDownloadURL(snapshot.ref);
    data.image = imageUrl;
  }

  if (!recipeRow.id && recipeRow.name) {
    if (
      isNaN(data.calories) ||
      data.calories === "" ||
      data.calories === null
    ) {
      delete data.calories;
    }
    if (
      isNaN(data.calories) ||
      data.weight === "" ||
      data.weight === null
    ) {
      delete data.weight;
    }
    const recipeName =
      typeof recipeRow.name === "object"
        ? recipeRow.name?.value
        : recipeRow.name;
    if (recipeName) {
      const q = query(
        collection(firestore, "recipe"),
        where("name", "==", recipeName),
        where("adminId", "==", uid)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        throw new Error(
          `Recipe with this name ${recipeName} already exists, choose different name`
        );
      }
    }
    const recipeRef = await addDoc(collection(firestore, "recipe"), {
      ...data,
      createdAt: new Date(),
      adminId: uid,
    });
    return stripUndefinedDeep({
      id: recipeRef.id,
      ...data,
      time: recipeRow.time,
    });
  }
  if (recipeRow.name) {
    return stripUndefinedDeep({
      id: recipeRow.id,
      ...data,
      time: recipeRow.time,
    });
  }
  return null;
}

async function buildDayPlansForSave(dayPlans, uid) {
  const out = {};
  const dayScheduleKeys = {};

  for (const dateKey of Object.keys(dayPlans || {})) {
    const dayObj = dayPlans[dateKey];
    if (!dayObj || typeof dayObj !== "object") continue;
    out[dateKey] = {};
    const keysUsed = [];
    for (const scheduleKey of Object.keys(dayObj)) {
      if (META.has(scheduleKey)) continue;
      const arr = dayObj[scheduleKey];
      if (!Array.isArray(arr) || arr.length === 0) continue;
      if (arr.length > PER_DAY_SOFT_MAX_RECIPES_PER_SLOT) {
        throw new Error(
          `Too many items in ${scheduleKey} on ${dateKey}. Max ${PER_DAY_SOFT_MAX_RECIPES_PER_SLOT}.`
        );
      }
      const built = [];
      for (let j = 0; j < arr.length; j++) {
        const row = await processRecipeRow(arr[j], uid);
        if (row) built.push(row);
      }
      if (built.length > 0) {
        out[dateKey][scheduleKey] = built;
        keysUsed.push(scheduleKey);
      }
    }
    if (keysUsed.length) dayScheduleKeys[dateKey] = keysUsed;
  }
  return { dayPlans: out, dayScheduleKeys };
}

/**
 * Persists a differentiated multi-day chart: layoutVariant + dayPlans map.
 */
export async function savePerDayRangeDiet({
  formdata,
  dietPlan,
  uid,
  dayPlans,
}) {
  const from = dietPlan.from;
  const to = dietPlan.to;
  const v = validatePerDayDateRange(from, to);
  if (!v.ok) throw new Error(v.message);

  const { dayPlans: builtPlans, dayScheduleKeys } = await buildDayPlansForSave(
    dayPlans,
    uid
  );

  const clientId = formdata.clientName.value;
  const clientDietPlan = {
    clientId,
    date: from,
    from,
    to,
    notes: dietPlan.notes,
    weekPlan: dietPlan.weekPlan.value,
    chartType: dietPlan.chartType.value,
    createdAt: new Date(),
    layoutVariant: LAYOUT_VARIANT_PER_DAY,
    dayPlans: builtPlans,
    dayScheduleKeys,
  };

  const ref = await addDoc(collection(firestore, "dietplan"), clientDietPlan);

  try {
    await sendDietChartWhatsAppNotification(clientId, ref.id, {
      sendNotification: true,
      templateId: WHATSAPP_TEMPLATE_TYPES.DIET_CHART_CREATED,
    });
  } catch (e) {
    console.warn("WhatsApp notification failed:", e);
  }

  return ref.id;
}

/**
 * Updates an existing per-day chart document (replaces dayPlans).
 */
export async function updatePerDayRangeDiet({
  formdata,
  dietPlan,
  uid,
  dayPlans,
}) {
  const dietId = dietPlan.dietChartId;
  const from = dietPlan.from;
  const to = dietPlan.to;
  const v = validatePerDayDateRange(from, to);
  if (!v.ok) throw new Error(v.message);

  const { dayPlans: builtPlans, dayScheduleKeys } = await buildDayPlansForSave(
    dayPlans,
    uid
  );

  const dietRef = doc(firestore, "dietplan", dietId);
  const updatePayload = {
    clientId: formdata.clientName.value,
    date: from,
    from,
    to,
    notes: dietPlan.notes,
    weekPlan: dietPlan.weekPlan?.value,
    chartType: dietPlan.chartType?.value,
    createdAt: dietPlan.createdAt ?? new Date(),
    layoutVariant: LAYOUT_VARIANT_PER_DAY,
    dayPlans: builtPlans,
    dayScheduleKeys,
  };

  await updateDoc(dietRef, updatePayload);

  return { dietChartId: dietId, ...dietPlan };
}
